import { createContext, createElement, useContext, type CSSProperties, type ReactElement } from 'react';
import { ImageWithLoader } from './ImageLoading';

/**
 * EvidenceImage: the one <img> every shared finding component renders a screenshot through, so each surface can
 * resolve a stored upload URL for its own runtime (style sweep 2026-10-06).
 *
 * Uploads are stored with the production host (server lib/storedImageBaseUrl.ts). On a dev API that host has no
 * such key, so finding screenshots rendered broken on the extension and team review in every local run. A surface
 * supplies its resolver through EvidenceImageProvider: the extension routes through useStoredImageSrc (which also
 * handles Chrome's local-network prompt), team review swaps the host for its own API origin. With no provider the
 * stored URL renders unchanged, which is what production needs.
 */
export interface EvidenceImageProps {
  src: string;
  alt: string;
  className?: string;
  style?: CSSProperties;
  loading?: 'lazy' | 'eager';
  fill?: boolean;
}
export type EvidenceImageComponent = (props: EvidenceImageProps) => ReactElement | null;

const PlainImage: EvidenceImageComponent = (props) => createElement(ImageWithLoader, props);
const EvidenceImageContext = createContext<EvidenceImageComponent>(PlainImage);
export const EvidenceImageProvider = EvidenceImageContext.Provider;

export function EvidenceImage(props: EvidenceImageProps): ReactElement | null {
  const Image = useContext(EvidenceImageContext);
  return createElement(Image, props);
}

/** The base the server stamps on every stored upload (lib/storedImageBaseUrl.ts). */
const STORED_UPLOADS_PREFIX = 'https://api.overlayqa.com/uploads/';

/** Swap the production host for `apiOrigin` on our own upload URLs; a no-op on production and for anything else. */
export function resolveStoredEvidenceUrl(url: string, apiOrigin: string): string {
  const origin = apiOrigin.replace(/\/+$/, '');
  if (origin === 'https://api.overlayqa.com' || !url.startsWith(STORED_UPLOADS_PREFIX)) return url;
  return `${origin}${url.slice('https://api.overlayqa.com'.length)}`;
}
